import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { Button } from "../ui";
import { NAV_TARGET_ATTRIBUTE } from "../navigation";
import { REVEAL_NAV_TARGET_EVENT, type GuideNode } from "./guideData";

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_WIDTH = 320;
const GAP = 12;

/**
 * Walks the user through real sidebar menus: each step navigates to the page and
 * rings the matching link. The page stays interactive on purpose, so the tour can
 * be read while actually looking at the screen it describes.
 */
export function GuideTour({
  nodes,
  onExit,
  onBackToDiagram,
}: {
  nodes: GuideNode[];
  onExit: () => void;
  onBackToDiagram?: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<TargetRect | null>(null);
  const [searchedLongEnough, setSearchedLongEnough] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const step = nodes[index];
  const total = nodes.length;

  const goNext = useCallback(() => {
    setIndex((current) => (current + 1 < total ? current + 1 : current));
  }, [total]);
  const goPrev = useCallback(() => setIndex((current) => Math.max(0, current - 1)), []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(REVEAL_NAV_TARGET_EVENT, { detail: { to: step?.to ?? null } }),
    );
  }, [step?.to]);

  useEffect(() => {
    if (step?.to) navigate(step.to);
  }, [navigate, step?.to]);

  useLayoutEffect(() => {
    setSearchedLongEnough(false);
    if (!step?.to) {
      setRect(null);
      return;
    }
    let broughtIntoView = false;
    const measure = () => {
      const element = document.querySelector<HTMLElement>(
        `[${NAV_TARGET_ATTRIBUTE}="${step.to}"]`,
      );
      // A link inside a closed dropdown is in the DOM but has no box yet.
      const initialBox = element?.getBoundingClientRect();
      if (!element || !initialBox || (initialBox.width === 0 && initialBox.height === 0)) {
        setRect(null);
        return;
      }
      if (!broughtIntoView) {
        broughtIntoView = true;
        element.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      const box = element.getBoundingClientRect();
      setRect({ top: box.top, left: box.left, width: box.width, height: box.height });
    };
    measure();
    // The sidebar may still be expanding, and the page it navigated to still
    // rendering, so keep re-measuring rather than trusting the first reading.
    const interval = window.setInterval(measure, 200);
    // Only claim the menu is missing once it has had time to appear.
    const grace = window.setTimeout(() => setSearchedLongEnough(true), 1200);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(grace);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step?.to]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onExit();
        return;
      }
      // The page underneath stays usable during the tour, so arrow keys must
      // only step the tour when the tour card itself holds focus.
      if (!cardRef.current?.contains(document.activeElement)) return;
      if (event.key === "ArrowRight") goNext();
      else if (event.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [goNext, goPrev, onExit]);

  if (!step) return null;

  const isLast = index === total - 1;
  const cardStyle = rect
    ? clampToViewport({
        left: rect.left + rect.width + GAP,
        top: rect.top - 8,
      })
    : undefined;

  return (
    <div className="pointer-events-none fixed inset-0 z-[60]">
      {rect ? (
        <div
          className="absolute rounded-md ring-2 ring-brand-500 ring-offset-2 transition-all duration-200"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          aria-hidden="true"
        />
      ) : null}
      <div
        ref={cardRef}
        role="dialog"
        aria-live="polite"
        aria-label={t("sysGuideTitle")}
        className={`pointer-events-auto absolute w-80 rounded-xl border border-slate-200 bg-white p-4 shadow-2xl ${
          cardStyle ? "" : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        }`}
        style={cardStyle}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-600">
            {t("sysGuideTourStep", { current: index + 1, total })}
          </span>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100"
            aria-label={t("sysGuideClose")}
            title={t("sysGuideClose")}
            onClick={onExit}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <h2 className="mt-1 text-sm font-bold text-slate-800">{t(step.labelKey)}</h2>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">{t(step.descKey)}</p>
        {!rect && searchedLongEnough ? (
          <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
            {t("sysGuideTourMissing")}
          </p>
        ) : null}
        <div className="mt-3 flex items-center justify-between gap-2">
          {onBackToDiagram ? (
            <button
              type="button"
              className="text-xs font-medium text-slate-500 underline-offset-2 hover:text-brand-700 hover:underline"
              onClick={onBackToDiagram}
            >
              {t("sysGuideBackToDiagram")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={goPrev}>
                {t("sysGuidePrev")}
              </Button>
            ) : null}
            <Button
              className="px-2 py-1 text-xs"
              onClick={isLast ? onExit : goNext}
            >
              {isLast ? t("sysGuideFinish") : t("sysGuideNext")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function clampToViewport({ left, top }: { left: number; top: number }) {
  const maxLeft = window.innerWidth - CARD_WIDTH - GAP;
  const maxTop = window.innerHeight - 220;
  return {
    left: Math.max(GAP, Math.min(left, Math.max(GAP, maxLeft))),
    top: Math.max(GAP, Math.min(top, Math.max(GAP, maxTop))),
  };
}
